// PromptChatBox.tsx
import { useEffect, useRef, useState } from "react";
import AppButton from "@/components/structure/AppButton";

import Markdown from 'react-markdown'
import { TaskPrompt, TaskResponse, TaskPromptResponse } from "@/api/types";
import { apiClient } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface PrompInterviewtChatBoxProps {
  prompt: TaskPrompt;
  taskResponse: TaskResponse;
  taskPromptResponse: TaskPromptResponse;
  onInterviewConfirmed?: () => Promise<void> | void;
}

type InterviewPhase =
  | "clarification"
  | "clarification_followup"
  | "interview"
  | "wrap_up_confirmation"
  | "wrap_up_rejection_reason"
  | "wrap_up_rejection_followup"
  | "done";

interface InterviewTurn {
  id: string;
  kind: "head" | "clarification" | "interview" | "wrap_up";
  question: { id: string; question: string };
  answer: { id: string; answer: string };
}

interface InterviewConversation {
  id: string;
  interviewType?: string;
  interview_type?: string;
  turns: InterviewTurn[];
  finalAnswer?: { id: string; answer: string } | null;
  final_answer?: { id: string; answer: string } | null;
}

interface InterviewState {
  phase: InterviewPhase;
  pending?: {
    text: string;
    kind?: string;
    options?: Record<string, string> | null;
  } | null;
  conversation: InterviewConversation;
}

interface BeginInterviewResponse {
  sessionId?: string;
  session_id?: string;
  state: InterviewState;
}

interface AdvanceInterviewResponse {
  sessionId?: string;
  session_id?: string;
  state: InterviewState;
  stageDurations?: Record<string, number>;
  stage_durations?: Record<string, number>;
}

interface ChatMessage {
  id: string;
  author: string;
  content: string;
  isSystem: boolean;
}

const normalizeInterviewState = (state: InterviewState): InterviewState => {
  const conversation = state.conversation;

  return {
    ...state,
    conversation: {
      ...conversation,
      interviewType: conversation.interviewType || conversation.interview_type,
      finalAnswer: conversation.finalAnswer ?? conversation.final_answer ?? null,
    },
    pending: state.pending
      ? {
          ...state.pending,
          options: state.pending.options ?? null,
        }
      : null,
  };
};

export default function PromptChatBox({
  prompt,
  taskResponse,
  taskPromptResponse,
  onInterviewConfirmed,
}: PrompInterviewtChatBoxProps) {
  const { user, isAuthenticated } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [interviewState, setInterviewState] = useState<InterviewState | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const inputBox = useRef<HTMLTextAreaElement>(null);

  const topContainer = useRef<HTMLDivElement>(null);

  const buildInitialQuestion = () => {
    console.log("prompt:", prompt);
    console.log("taskResponse:", taskResponse);

    let result = null;

    if (prompt?.description && prompt.description.trim() !== "" && prompt.description.trim().toLowerCase() !== "<p></p>") {
      result = prompt.description;
    }
    else if (prompt?.header && prompt.header.trim() !== "") {
      result = prompt.header;
    }
    else if (prompt?.name && prompt.name.trim() !== "") {
      result = prompt.name;
    }

    if (result) {
      return `Question: ${result}`;
    }

    return `Prompt response ${taskPromptResponse?.name || taskPromptResponse?.id}`;
  };

  const buildInitialResponse = () => {
    const responseText = taskPromptResponse?.responseText?.trim();
    if (responseText && responseText !== "") {
      return responseText;
    }
    return "No response text was provided.";
  };

  const buildLadderingSeed = (responseText: string) => {
    const trimmed = responseText.trim();
    if (!trimmed) {
      return "initial-response";
    }
    return trimmed.slice(0, 120);
  };

  const mapStateToMessages = (state: InterviewState): ChatMessage[] => {
    const mapped: ChatMessage[] = [];

    state.conversation?.turns?.forEach((turn) => {
      if (turn.question?.question) {
        mapped.push({
          id: `${turn.id}-q`,
          author: "LLM",
          content: turn.question.question,
          isSystem: true,
        });
      }
      if (turn.answer?.answer) {
        mapped.push({
          id: `${turn.id}-a`,
          author: turn.kind === "head" ? "Interviewee" : (user?.username || "You"),
          content: turn.answer.answer,
          isSystem: false,
        });
      }
    });

    if (state.pending?.text) {
      mapped.push({
        id: `pending-${state.phase}-${state.conversation.turns.length}`,
        author: "LLM",
        content: state.pending.text,
        isSystem: true,
      });
    }

    if (state.phase === "done" && state.conversation.finalAnswer?.answer) {
      mapped.push({
        id: `final-answer-${state.conversation.finalAnswer.id}`,
        author: "LLM",
        content: state.conversation.finalAnswer.answer,
        isSystem: true,
      });
    }

    return mapped;
  };

  const initializeSession = async () => {
    const initialResponse = buildInitialResponse();
    const initialQuestion = buildInitialQuestion();
    const generatedSessionId = crypto.randomUUID();

    const payload = {
      sessionId: generatedSessionId,
      mode: "general_qa",
      ladderingConfig: "ACV",
      contextType: "plain_text",
      contextPlainText: {
        initialQuestion,
        initialResponse,
        ladderingSeed: buildLadderingSeed(initialResponse),
        additionalContext: [
          `task_response_id=${taskResponse.id}`,
          `task_prompt_response_id=${taskPromptResponse.id}`,
        ],
      },
      question: initialQuestion,
      answer: initialResponse,
      ladderingSeed: buildLadderingSeed(initialResponse),
      interviewer: {
        modelStr: "gpt-4.1-mini",
      },
      judge: {
        modelStr: "claude-sonnet-5",
      },
    };

    const beginResponse = await apiClient.post<BeginInterviewResponse>(
      "/interview/begin/",
      payload,
    );
    const normalizedState = normalizeInterviewState(beginResponse.state);
    const normalizedSessionId = beginResponse.sessionId || beginResponse.session_id || generatedSessionId;

    setSessionId(normalizedSessionId);
    setInterviewState(normalizedState);
    setMessages(mapStateToMessages(normalizedState));
  };

  const submitAnswer = async (message: string) => {
    if (!sessionId || !interviewState) {
      return;
    }

    const isWrapUpYes =
      interviewState.phase === "wrap_up_confirmation" &&
      message.trim().toLowerCase() === "yes";

    const optimisticUserMessage: ChatMessage = {
      id: `pending-${Date.now()}`,
      author: user?.username || "You",
      content: message,
      isSystem: false,
    };
    setMessages((prev) => [...prev, optimisticUserMessage]);

    const payload = {
      sessionId,
      state: interviewState,
      input: message,
    };

    const advanceResponse = await apiClient.post<AdvanceInterviewResponse>(
      "/interview/advance/",
      payload,
    );
    const normalizedState = normalizeInterviewState(advanceResponse.state);

    setSessionId(advanceResponse.sessionId || advanceResponse.session_id || sessionId);
    setInterviewState(normalizedState);
    setMessages(mapStateToMessages(normalizedState));

    if (isWrapUpYes && normalizedState.phase === "done") {
      await onInterviewConfirmed?.();
    }
  };

  const getWrappedLines = (textarea: HTMLTextAreaElement) => {
    const style = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(style.lineHeight);

    if (isNaN(lineHeight)) {
        // fallback if line-height is "normal"
        const fontSize = parseFloat(style.fontSize);
        // approximate normal line height as 1.2 × font-size
        return Math.round(textarea.scrollHeight / (fontSize * 1.2));
    }

    return Math.round(textarea.scrollHeight / lineHeight);
  }

  const resizeInputBox = () => {
    // set the height of the textarea based on content
    if (inputBox.current) {
      const lines = getWrappedLines(inputBox.current);
      const maxLines = 12;
      inputBox.current.style.height = 'auto'; // reset height
      inputBox.current.style.height = `${Math.min(lines, maxLines) * 1.2}em`; // set new height
    }
  }

  useEffect(() => {
    resizeInputBox();
  }, [input]);

  useEffect(() => {
    let isCancelled = false;

    const run = async () => {
      try {
        setErrorMessage(null);
        if (!isAuthenticated) {
          return;
        }
        await initializeSession();
      } catch (err) {
        if (!isCancelled) {
          console.error("Failed to initialize interview session:", err);
          setErrorMessage("Failed to initialize interview chat.");
        }
      }
    };

    run();
    return () => {
      isCancelled = true;
    };
  }, [taskResponse?.id, taskPromptResponse?.id, isAuthenticated]);

  const handleSendMessage = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || !interviewState || !sessionId) {
      return;
    }

    try {
      setErrorMessage(null);
      setIsSubmitting(true);
      await submitAnswer(trimmed);
      setInput("");
    } catch (err) {
      console.error("Failed to submit interview answer:", err);
      setErrorMessage("Failed to submit answer.");
    } finally {
      setIsSubmitting(false);
      if (inputBox.current) {
        inputBox.current.focus();
      }
    }
  };

  // while a response is being received, keep the parent of topContainer scrolled to the bottom
  useEffect(() => {
    if (messages.length > 0 || isSubmitting) {
      const interval = setInterval(() => {
        if (topContainer.current) {
          const parent = topContainer.current.parentElement;
          if (parent) {
            // parent.scrollTop = parent.scrollHeight;
            // smooth scroll to bottom
            parent.scrollTo({
              top: parent.scrollHeight,
              behavior: 'smooth',
            });
          }
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [messages, isSubmitting]);

  const isTerminal = interviewState?.phase === "done";
  const pendingOptions = interviewState?.pending?.options
    ? Object.entries(interviewState.pending.options)
    : [];

  return (
    <div className="w-full" ref={topContainer}>
      {/* Conversation to this point, included in the review_messages field in taskResponse */ }
      <div>
        <div>
          <h3>Interview Chat for Prompt Response ID: {taskPromptResponse?.name}</h3>

        </div>
        <div className="p-2 my-2 flex flex-col space-y-2 w-full">
          { /* Shows the history of messages for this review */ }
          {messages.map((msg) => (
            <div key={msg.id} className={`mb-3 ${!msg.isSystem ? 'bg-gray-100 self-end p-3 rounded-xl w-3/4' : 'bg-gray-600 text-white self-start p-3 rounded-xl w-3/4'}`}>
              <div className={`text-sm text-gray-600 mb-1 ${msg.isSystem ? 'text-white' : ''}`}>
                <strong>{ msg.isSystem ? "LLM" : msg.author}</strong>
              </div>
              <div className="preflight chat-content">
                <Markdown>{msg.content}</Markdown>
              </div>
            </div>
          ))}

          {isSubmitting && (
            <div key="incoming-system-msg" className={`mb-3 bg-gray-600 text-white self-start p-3 rounded-xl w-3/4`}>
              <div className={`text-sm mb-1 text-white`}>
                <strong>LLM</strong> <span className="text-xs italic">processing...</span>
              </div>
              <div>
                <FontAwesomeIcon icon="spinner" spin />
              </div>
            </div>
          )}

          {errorMessage && (
            <div className="text-red-600 text-sm">{errorMessage}</div>
          )}

          {!isTerminal && pendingOptions.length > 0 && (
            <div className="self-start w-3/4 space-y-2">
              {pendingOptions.map(([optionKey, optionText]) => (
                <button
                  key={`option-${optionKey}`}
                  type="button"
                  onClick={() => handleSendMessage(optionKey.toLowerCase())}
                  disabled={isSubmitting || !interviewState || !sessionId}
                  className="w-full text-left border border-gray-300 rounded-xl px-3 py-2 bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <span className="font-semibold mr-2">{optionKey}.</span>
                  <span>{optionText}</span>
                </button>
              ))}
            </div>
          )}

        </div>

        {/* <div>
          Status: {status}
        </div> */}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage(input);
          }}
          className="flex items-end mb-6 w-full border border-gray-300 rounded-xl p-2"
        >
          <textarea
            ref={inputBox}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // make Enter send the message, unless Shift is held in which case insert a newline
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (input && input.trim() != "" && !isSubmitting && !isTerminal) {
                  handleSendMessage(input);
                }
              }
            }}
            className="w-full p-2 pr-3 focus:outline-none"
            placeholder={isTerminal ? "Interview completed." : "Type something..."}
            disabled={isSubmitting || isTerminal || !interviewState || !sessionId}
          />
          <AppButton
            type="submit"
            variant="primary"
            className="ml-2 rounded-xl"
            forwardArrow={true}
            disabled={!input || isSubmitting || isTerminal || !interviewState || !sessionId}
          >
            {!isSubmitting ? "Send" : <>
              <FontAwesomeIcon icon="spinner" spin className="mr-2" /> processing
            </>}
          </AppButton>
        </form>
      </div>
    </div>
  );
}
